/**
 * GenerationStepsTable — reviewer-friendly step table for the expert
 * "Generation internals" panel.
 *
 * Renders one generation attempt's log entries as a scannable table:
 * # · Time · Section · Action (plain language via stepLabels) · Result ·
 * Rating. Clicking a row expands a detail row showing the step's full
 * input/output as labeled plain text ("Asked:" / "Got back:"), never JSON
 * styling. The raw tool name only ever appears as a title tooltip.
 *
 * Rating (thumbs + note) keeps the exact feedback contract that previously
 * lived in AgentStepTimeline — onSubmit(stepIndex, verdict, note?,
 * expectedOutput?) keyed by the ABSOLUTE log index, server-computed is_own
 * detection with an author email fallback, and a note popover that pre-syncs
 * to the latest own verdict at the moment it opens. The popover captures TWO
 * distinct fields: a note (critique) and an expected output (training
 * exemplar for the few-shot/RAG loop).
 */

import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquarePlus, GraduationCap } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { humanizeStepLabel } from './stepLabels';
import { humanizeResultDisplay } from './resultDisplay';
import type { GenerationAttemptEntry, StepFeedback } from './types';

/** Expert per-step feedback wiring (same contract AgentStepTimeline carried) */
export interface StepsTableFeedbackConfig {
  /** All authors' feedback rows keyed by absolute log index */
  byIndex: Record<number, StepFeedback[]>;
  /** Author identity of the current user (marks their verdict as selected) */
  currentAuthor?: string;
  /**
   * note = critique; expectedOutput = training exemplar (what the step should
   * have produced instead). Quick thumb clicks send neither — both undefined —
   * so the backend upsert preserves stored values.
   */
  onSubmit: (
    stepIndex: number,
    verdict: 'up' | 'down',
    note?: string,
    expectedOutput?: string,
  ) => void;
}

interface GenerationStepsTableProps {
  /** All log entries of ONE attempt, in order (absolute `index` included) */
  entries: GenerationAttemptEntry[];
  /** Optional rating controls; absent → read-only table */
  feedback?: StepsTableFeedbackConfig;
}

function isErrorEntry(entry: GenerationAttemptEntry): boolean {
  return (
    entry.level === 'warn' ||
    entry.level === 'warning' ||
    entry.level === 'error' ||
    entry.detail?.is_error === true
  );
}

/** elapsed_ms → "m:ss" (minutes don't wrap: 72:15 for long runs) */
function formatClock(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Verdict/note controls for a single step (expert feedback mode) */
const StepRatingControls: React.FC<{
  stepIndex: number;
  rows: StepFeedback[];
  currentAuthor?: string;
  onSubmit: StepsTableFeedbackConfig['onSubmit'];
}> = ({ stepIndex, rows, currentAuthor, onSubmit }) => {
  // Server-computed `is_own` (JWT author match) wins; fall back to the email
  // comparison for older rows where `is_own` is absent (backwards compatible).
  const own = rows.find(
    (row) =>
      row.is_own === true ||
      (row.is_own === undefined && currentAuthor != null && row.author === currentAuthor),
  );

  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [expectedOutput, setExpectedOutput] = useState('');
  const [noteVerdict, setNoteVerdict] = useState<'up' | 'down'>(own?.verdict ?? 'up');

  // `own` arrives async (feedback query resolves after mount), so the state
  // initializer above can be stale. Re-sync at the moment the popover opens
  // so the note always defaults to the latest known own-verdict.
  const handleNoteOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setNoteVerdict(own?.verdict ?? 'up');
    setNoteOpen(nextOpen);
  };

  const tooltip = rows.length > 0
    ? rows
        .map((row) => `${row.author}: ${row.verdict}${row.note ? ` — ${row.note}` : ''}`)
        .join('\n')
    : undefined;

  const upCount = rows.filter((row) => row.verdict === 'up').length;
  const downCount = rows.filter((row) => row.verdict === 'down').length;

  // Rows carrying a training exemplar — surfaced as a subtle row indicator
  const exemplarRows = rows.filter((row) => row.expected_output);
  const exemplarTooltip =
    exemplarRows.length > 0
      ? exemplarRows
          .map((row) => `${row.author}: ${row.expected_output}`)
          .join('\n')
      : undefined;

  const submitNote = () => {
    onSubmit(
      stepIndex,
      noteVerdict,
      note.trim() || undefined,
      expectedOutput.trim() || undefined,
    );
    setNoteOpen(false);
    setNote('');
    setExpectedOutput('');
  };

  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={tooltip}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label="Rate step up"
        onClick={() => onSubmit(stepIndex, 'up')}
        className={`p-0.5 rounded hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
          own?.verdict === 'up' ? 'text-teal-dark' : 'text-slate-300 hover:text-muted-foreground'
        }`}
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      {upCount > 0 && <span className="text-[10px] text-muted-foreground">{upCount}</span>}
      <button
        type="button"
        aria-label="Rate step down"
        onClick={() => onSubmit(stepIndex, 'down')}
        className={`p-0.5 rounded hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
          own?.verdict === 'down' ? 'text-destructive' : 'text-slate-300 hover:text-muted-foreground'
        }`}
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
      {downCount > 0 && <span className="text-[10px] text-muted-foreground">{downCount}</span>}
      <Popover open={noteOpen} onOpenChange={handleNoteOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Add feedback note"
            className="p-0.5 rounded text-slate-300 hover:text-muted-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
          >
            <MessageSquarePlus className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2.5 space-y-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Note verdict up"
              onClick={() => setNoteVerdict('up')}
              className={`p-1 rounded transition-colors ${
                noteVerdict === 'up'
                  ? 'text-teal-dark bg-teal-light'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              aria-label="Note verdict down"
              onClick={() => setNoteVerdict('down')}
              className={`p-1 rounded transition-colors ${
                noteVerdict === 'down'
                  ? 'text-destructive bg-destructive/10'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
            <span className="text-[11px] text-muted-foreground">Step feedback</span>
          </div>
          <label className="block space-y-1">
            <span className="text-[11px] font-medium text-muted-foreground">Feedback note</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What's wrong (or right) with this step?"
              aria-label="Feedback note"
              rows={2}
              className="w-full text-xs border border-border rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-teal"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-medium text-muted-foreground">Expected output</span>
            <textarea
              value={expectedOutput}
              onChange={(e) => setExpectedOutput(e.target.value)}
              aria-label="Expected output"
              rows={3}
              className="w-full text-xs border border-border rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-teal"
            />
            <span className="block text-[10px] leading-snug text-muted-foreground">
              What this step should have produced instead — used as a training example.
            </span>
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={submitNote}
              className="text-xs px-2.5 py-1 rounded bg-teal-light text-teal-dark hover:bg-teal hover:text-white transition-colors font-medium"
            >
              Submit
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {exemplarRows.length > 0 && (
        <span
          aria-label="Expected output recorded"
          title={exemplarTooltip}
          className="p-0.5 text-teal-dark"
        >
          <GraduationCap className="w-3 h-3" />
        </span>
      )}
    </span>
  );
};

const HEADER_CELL =
  'px-2 py-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap';

const GenerationStepsTable: React.FC<GenerationStepsTableProps> = ({ entries, feedback }) => {
  const [openRows, setOpenRows] = useState<Set<number>>(new Set());

  if (entries.length === 0) {
    return (
      <div className="px-3 py-4 text-xs text-muted-foreground">
        No steps were recorded for this attempt.
      </div>
    );
  }

  const toggleRow = (index: number) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    // Scroll (both axes) stays INSIDE the panel — the page never scrolls
    // sideways; sticky header keeps columns readable across ~150 rows.
    <div className="max-h-[28rem] overflow-auto">
      <table className="w-full min-w-[42rem] border-collapse text-xs">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr className="border-b border-border">
            <th scope="col" className={`${HEADER_CELL} w-8 text-right`}>#</th>
            <th scope="col" className={`${HEADER_CELL} w-12 text-right`}>Time</th>
            <th scope="col" className={HEADER_CELL}>Section</th>
            <th scope="col" className={HEADER_CELL}>Action</th>
            <th scope="col" className={HEADER_CELL}>Result</th>
            <th scope="col" className={`${HEADER_CELL} w-20`}>Rating</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, position) => {
            const failed = isErrorEntry(entry);
            const open = openRows.has(entry.index);
            const result = entry.detail?.output_summary;
            const prettyResult = result
              ? humanizeResultDisplay(result, entry.tool)
              : undefined;
            const showRating = feedback != null && entry.step === 'agent_step';

            return (
              <React.Fragment key={entry.index}>
                <tr
                  onClick={() => toggleRow(entry.index)}
                  onKeyDown={(e) => {
                    // Only when the row itself is focused — keystrokes inside
                    // child controls (rating buttons, note textarea) bubble up
                    // through the React tree and must not toggle the row.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleRow(entry.index);
                    }
                  }}
                  tabIndex={0}
                  aria-expanded={open}
                  className={`border-b border-border cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-teal ${
                    failed ? 'bg-destructive/5 hover:bg-destructive/10' : 'hover:bg-muted'
                  }`}
                >
                  <td className="px-2 py-1.5 text-right text-slate-300 tabular-nums align-top">
                    {position + 1}
                  </td>
                  <td className="px-2 py-1.5 text-right text-muted-foreground tabular-nums whitespace-nowrap align-top">
                    {formatClock(entry.elapsed_ms)}
                  </td>
                  <td
                    className="px-2 py-1.5 text-muted-foreground align-top max-w-[9rem] truncate"
                    title={entry.section || undefined}
                  >
                    {entry.section || ''}
                  </td>
                  <td
                    className={`px-2 py-1.5 align-top min-w-[16rem] max-w-[24rem] ${
                      failed ? 'text-destructive font-medium' : 'text-muted-foreground'
                    }`}
                    // Raw tool name is debugging info only — tooltip, never label
                    title={entry.tool || undefined}
                  >
                    {humanizeStepLabel(entry.msg)}
                  </td>
                  {/* Result takes the remaining room on wide panels.
                      Older logs store raw tool-result JSON — prettified at
                      render time; the cell never shows braces (raw stays
                      available in the detail row). */}
                  <td
                    className={`w-full px-2 py-1.5 align-top ${
                      failed ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                  >
                    {prettyResult ? (
                      <span className="line-clamp-2 break-words">{prettyResult}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1 align-top whitespace-nowrap">
                    {showRating && (
                      <StepRatingControls
                        stepIndex={entry.index}
                        rows={feedback.byIndex[entry.index] ?? []}
                        currentAuthor={feedback.currentAuthor}
                        onSubmit={feedback.onSubmit}
                      />
                    )}
                  </td>
                </tr>
                {open && (
                  <tr className={failed ? 'bg-destructive/5' : 'bg-muted/70'}>
                    <td className="border-b border-border" />
                    <td colSpan={5} className="border-b border-border px-2 py-2 space-y-1.5">
                      {entry.detail?.input_summary && (
                        <div className="text-[11px] leading-snug text-muted-foreground whitespace-pre-wrap break-words">
                          <span className="font-medium text-muted-foreground">Asked: </span>
                          {entry.detail.input_summary}
                        </div>
                      )}
                      {result && (
                        <div
                          className={`text-[11px] leading-snug whitespace-pre-wrap break-words ${
                            failed ? 'text-destructive' : 'text-muted-foreground'
                          }`}
                        >
                          <span className="font-medium text-muted-foreground">Got back: </span>
                          {prettyResult}
                        </div>
                      )}
                      {/* When the display was prettified (or extraction was
                          partial), keep the raw string available beneath it */}
                      {result && prettyResult !== result && (
                        <div className="text-[10px] leading-snug text-muted-foreground whitespace-pre-wrap break-words">
                          <span className="font-medium">Raw: </span>
                          {result}
                        </div>
                      )}
                      {!entry.detail?.input_summary && !entry.detail?.output_summary && (
                        <div className="text-[11px] text-muted-foreground">
                          This step didn&apos;t record a result.
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default GenerationStepsTable;
